// Inline save feedback, used by every per-field / per-control save.
//
//   const save = useSaveAction()
//   await save.run(() => api.tenants.update(id, { name }))
//   <SaveStatus state={save} onRetry={...} />
//
// States: idle → saving → saved (clears after 2.5s) | error (stays until the
// next attempt). Every control that writes shows its own outcome here, so a
// page never needs a global "Save" button.

import { cn } from '@/lib/format'
import { AlertIcon, CheckIcon, SpinnerIcon } from './icons'
import type { SaveAction } from './useSaveAction'

export interface SaveStatusProps {
  state: Pick<SaveAction, 'status' | 'error'>
  onRetry?: () => void
  className?: string
}

/** "Saving…" / "Saved" / "Couldn't save: …  Retry". Announced politely. */
export function SaveStatus({ state, onRetry, className }: SaveStatusProps) {
  return (
    <span role="status" aria-live="polite" className={cn('inline-flex min-h-[1.25rem] items-center gap-1.5 text-xs', className)}>
      {state.status === 'saving' && (
        <span className="inline-flex items-center gap-1.5 text-muted">
          <SpinnerIcon size={13} /> Saving…
        </span>
      )}
      {state.status === 'saved' && (
        <span className="inline-flex items-center gap-1 text-good">
          <CheckIcon size={13} /> Saved
        </span>
      )}
      {state.status === 'error' && (
        <span className="inline-flex flex-wrap items-center gap-1.5 text-bad">
          <AlertIcon size={13} />
          <span>{state.error ? `Couldn't save: ${state.error}` : "Couldn't save"}</span>
          {onRetry ? (
            <button type="button" onClick={onRetry} className="font-semibold text-ink underline underline-offset-2 hover:text-accent">
              Retry
            </button>
          ) : null}
        </span>
      )}
    </span>
  )
}
